import { useEffect, useState, useMemo } from "react";
import Uri from "jsuri";
import { AxiosRequestConfig, AxiosResponse } from "axios";
import { getRequest, toUrl } from "./getRequest";
import { CancelTokenMidleware } from "./CancelTokenMidleware";
import { LoadingMiddleware } from "./LoadingMiddleware";
import { flaskFetcher, fleetlogicFetcher, gatewayFetcher } from "./fetchers";

type CancelRequestConfig = Omit<AxiosRequestConfig, "cancelToken">;

interface RequestOptions {
  url?: string | Uri;
  config?: CancelRequestConfig;
  wait?: number;
  flask?: boolean;
  gateway?: boolean;
}

export function useRequest<
  ResponseData = any,
  Response extends AxiosResponse = AxiosResponse<ResponseData>
>({
  url,
  config = undefined,
  flask: isFlaskRequest = true,
  gateway: isGateWayRequest = false
}: RequestOptions = {}) {
  const [isLoading, setIsLoading] = useState(false);
  const requestUrl = toUrl(url);
  const cancelToken = CancelTokenMidleware();
  const loading = LoadingMiddleware(setIsLoading);
  const fetcher = useMemo(() => {
    if (isGateWayRequest) {
      return gatewayFetcher;
    }
    if (isFlaskRequest) {
      return flaskFetcher;
    }
    return fleetlogicFetcher;
  }, [isFlaskRequest, isGateWayRequest]);

  const [{ makeRequest, setUrl, hooks, cancel }] = useState(() =>
    getRequest<ResponseData, Response>(requestUrl, config, fetcher, [
      cancelToken,
      loading
    ])
  );

  useEffect(() => {
    setUrl(requestUrl);
  }, [requestUrl]);

  hooks.forEach(hook => hook());

  return [makeRequest, { cancel, isLoading, setIsLoading }] as const;
}
